import { useMemo } from 'react';

import { findPurchasablePlan } from 'src/components/AzarOnly/Subscription/utils';
import { getSubscriptionAction } from 'src/components/AzarOnly/Subscription/utils/getSubscriptionAction';
import { INVENTORY_ITEM_CATEGORY_ID_TYPES, InventoryItemCategoryId } from 'src/types/itemInventory';

import { useSubscriptionPageQuery } from './useSubscriptionPageQuery';
import { useSubscriptionStatus } from './useSubscriptionStatus';

type PaymentButtonType = 'purchase' | 'purchase_upgrade' | 'purchase_downgrade' | 'purchase_renew';

interface Props {
  pageId?: string;
}

/**
 * 구독 관련 이벤트 로깅에 공통으로 사용되는 파라미터
 */
export const useSubscriptionEventData = ({ pageId }: Props = {}) => {
  const { data: subscriptionData } = useSubscriptionPageQuery();
  const subscriptionStatus = useSubscriptionStatus();
  const { hasWebCoreSubscription, hasWebMaxSubscription, isWebCoreRenewOff, isWebMaxRenewOff } =
    subscriptionStatus;

  const subscribedPlan: InventoryItemCategoryId | undefined = hasWebMaxSubscription
    ? INVENTORY_ITEM_CATEGORY_ID_TYPES.AZAR_MAX
    : hasWebCoreSubscription
      ? INVENTORY_ITEM_CATEGORY_ID_TYPES.AZAR_CORE
      : undefined;

  const isRenewOff =
    subscribedPlan === INVENTORY_ITEM_CATEGORY_ID_TYPES.AZAR_MAX
      ? isWebMaxRenewOff
      : subscribedPlan === INVENTORY_ITEM_CATEGORY_ID_TYPES.AZAR_CORE
        ? isWebCoreRenewOff
        : null;

  const buttonType = useMemo<PaymentButtonType>(() => {
    if (!pageId || !subscriptionData) return 'purchase';

    const plan = findPurchasablePlan(subscriptionData.pages, pageId);
    if (!plan || !plan.planModule.categoryId) return 'purchase';

    const action = getSubscriptionAction({
      selectedPlan: plan.planModule.categoryId,
      subscriptionStatus,
    });

    if (action === 'renew') return 'purchase_renew';
    if (action === 'upgrade') return 'purchase_upgrade';
    if (action === 'downgrade') return 'purchase_downgrade';
    return 'purchase';
  }, [pageId, subscriptionData, subscriptionStatus]);

  const eventParams = {
    /** 구독중인 상품: azar_core | azar_max | none */
    subs_product: subscribedPlan === undefined ? 'none' : subscribedPlan.toLowerCase(),
    /**
     * 구독 상태: renew_on | renew_off | none
     * crossgrade_pending / downgrade_pending 은 웹에 없음
     */
    store_subscription_state:
      subscribedPlan === undefined ? 'none' : isRenewOff ? 'renew_off' : ('renew_on' as const),
  };

  return {
    // 버튼 타입: purchase | purchase_upgrade | purchase_downgrade | purchase_renew
    buttonType,
    // 구독중인 상품 티어
    subscribedPlan,
    /**
     * analytics eventParams에 spread할 이벤트 파라미터 전용 객체.
     * subscriptionEventData 전체를 spread하면 buttonType, subscribedTier 같은
     * camelCase 헬퍼 필드가 의도치 않게 이벤트에 포함되므로 이 객체만 사용한다.
     */
    eventParams,
  };
};
